import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    TextField,
    Button,
    CircularProgress,
    Box,
    InputAdornment,
    FormControlLabel,
    Switch,
    FormGroup,
} from "@mui/material";
import {
    fetchPortalUser,
    selectEmployeeForSettings,
    selectOperatorForSettings,
    selectUserForSettings,
    updateEmployee,
    updateOperator,
    updatePortalUser,
} from "../userSlice";
import { useTranslation } from "react-i18next";
import React from "react";
import AuthService from "src/components/AuthService";
import { CheckCircleOutlineTwoTone } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { ValidateUniquePin } from "src/services/ValidationService";

const Auth = new AuthService();

const adminPinGroups = [
    "SterilisSuperUsers",
    "SterilisUser",
    "SterilisPortalUsers",
    "SterilisWasteTypeAdmin",
    "FSEs",
    "ExternalFSEs",
    "FactoryWorkers",
    "DistributorAdmins",
    "DistributorFSEs",
    "DistributorReadOnly",
];

const EditUserSettings = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isLoading, tokenUser, group } = useSelector((state) => state.user);
    const user = useSelector(selectUserForSettings);
    const operator = useSelector(selectOperatorForSettings);
    const employee = useSelector(selectEmployeeForSettings);
    const [formData, setFormData] = useState({
        employee: {
            full_name: "",
            email_address: "",
            phone: "",
        },
        operator: {
            pin: "",
            notify_error: false,
            notify_complete: false,
        },
        user: {
            username: "",
        },
    });
    const [usernameValid, setUsernameValid] = useState(false);
    const [errors, setErrors] = useState({
        usernameValidationError: false,
        usernameUnavailable: false,
        pinValidationError: false,
        pinAvailableError: false,
    });

    const [pinMin, setPinMin] = useState(4);
    const [pinMax, setPinMax] = useState(7);

    const numWordArray = [
        "zero",
        "one",
        "two",
        "three",
        "four",
        "five",
        "six",
        "seven",
        "eight",
        "nine",
    ];

    useEffect(() => {
        dispatch(fetchPortalUser(tokenUser.user_id));
    }, []);

    useEffect(() => {
        if (group && adminPinGroups.includes(group)) {
            setPinMin(8);
            setPinMax(8);
        }
        if (user && Object.keys(user).length > 0) {
            setFormData({
                employee: {
                    full_name: employee?.full_name || "",
                    email_address: employee?.email_address || "",
                    phone: employee?.phone || "",
                },
                operator: {
                    pin: operator?.pin || "",
                    notify_complete: operator?.notify_complete || false,
                    notify_error: operator?.notify_error || false,
                },
                user: {
                    username: user.username || "",
                },
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes(".")) {
            const [parent, child] = name.split(".");
            setFormData((prev) => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value,
                },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

        if (name === "user.username") {
            if (/^[a-zA-Z0-9_-]*$/i.test(value)) {
                setErrors((prev) => ({ ...prev, usernameValidationError: false }));
                //checkIfUsernameTaken(value);
            } else {
                setErrors((prev) => ({ ...prev, usernameValidationError: true }));
            }
        }

        if (name === "operator.pin") {
            if (value.length < pinMin || value.length > pinMax || isNaN(value)) {
                setErrors((prev) => ({ ...prev, pinValidationError: true }));
            } else {
                setErrors((prev) => ({ ...prev, pinValidationError: false }));
            }
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (user && user.username) {
                if (
                    formData.user.username.length > 0 &&
                    !errors.usernameValidationError
                ) {
                    checkIfUsernameTaken(formData.user.username);
                }
            }
        }, 1000);

        return () => {
            clearTimeout(timeout);
        };
    }, [formData.user.username]);

    useEffect(() => {
        const timeout = setTimeout(async () => {
            if (operator && operator.pin) {
                if (formData.operator.pin !== operator.pin && !errors.pinValidationError) {
                    if (await ValidateUniquePin(formData.operator.pin, employee.group, employee.customer_id)) {
                        setErrors((prev) => ({ ...prev, pinAvailableError: false }));
                    } else {
                        setErrors((prev) => ({ ...prev, pinAvailableError: true }));
                    }
                }
            }
        }, 1000);

        return () => {
            clearTimeout(timeout);
        };
    }, [formData.operator.pin]);

    const checkIfUsernameTaken = (username) => {
        if (username !== user.username) {
            Auth.fetch(`/api/username-available/`, {
                method: "POST",
                body: JSON.stringify({
                    username: username,
                }),
            })
                .then((data) => {
                    if (data["available"] === 1) {
                        setUsernameValid(true);
                        setErrors((prev) => ({ ...prev, usernameUnavailable: false }));
                    } else {
                        setUsernameValid(false);
                        setErrors((prev) => ({ ...prev, usernameUnavailable: true }));
                    }
                })
                .catch((err) => {
                    //Sentry.captureException(err);
                });
        } else {
            setUsernameValid(false);
            setErrors((prev) => ({
                ...prev,
                usernameValidationError: false,
                usernameUnavailable: false,
            }));
        }
    };

    const getPinValidationMessage = () => {
        if (errors.pinAvailableError) {
            return t(`PIN Is Already In Use.`);
        }
        if (pinMin === pinMax) {
            return t(`PINs must be ${numWordArray[pinMin]} (${pinMin}) digits long.`);
        }
        return t(
            `PINs must be between ${numWordArray[pinMin]} (${pinMin}) and ${numWordArray[pinMax]} (${pinMax}) digits long.`
        );
    };

    const getUsernameValidationMessage = () => {
        if (errors.usernameValidationError) {
            return t(`Usernames must be alphanumeric. - and _ are allowed.`);
        }
        if (errors.usernameUnavailable) {
            return t(`Username ${formData.user.username} already taken.`);
        }
        if (usernameValid) {
            return t(`Username ${formData.user.username} is available.`);
        }
        return "";
    };

    const checkDirtyFields = (obj, original) => {
        const keys = Object.keys(obj);
        const originalKeys = Object.keys(original).filter((key) => key !== "id");
        const isDirty = keys.some((key) => {
            if (typeof obj[key] === "object") {
                return checkDirtyFields(obj[key], original[key]);
            } else {
                return obj[key] !== original[key];
            }
        });
        return isDirty;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!errors.usernameValidationError && !errors.pinValidationError) {
            if (checkDirtyFields(formData.user, user)) {
                dispatch(updatePortalUser({ userId: user.id, user: formData.user }))
                    .then(() => {
                        enqueueSnackbar(t("Successfully edited user details."), {
                            variant: "success",
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(
                            t(`Failed to edit user details. Reason: ${err.message}`),
                            { variant: "error" }
                        );
                    });
            }
            if (checkDirtyFields(formData.employee, employee)) {
                dispatch(
                    updateEmployee({
                        employeeId: employee.id,
                        employee: formData.employee,
                    })
                )
                    .then(() => {
                        enqueueSnackbar(t("Successfully edited employee details."), {
                            variant: "success",
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(
                            t(`Failed to employee account details. Reason: ${err.message}`),
                            { variant: "error" }
                        );
                    });
            }
            if (checkDirtyFields(formData.operator, operator)) {
                dispatch(
                    updateOperator({
                        operatorId: operator.id,
                        operator: formData.operator,
                    })
                )
                    .then(() => {
                        enqueueSnackbar(t("Successfully edited operator details."), {
                            variant: "success",
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(
                            t(`Failed to edit operator details. Reason: ${err.message}`),
                            { variant: "error" }
                        );
                    });
            }
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ maxWidth: 800, mx: "auto" }}
        >
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    <TextField
                        fullWidth
                        label={t("Full Name")}
                        name="employee.full_name"
                        value={formData.employee.full_name}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label={t("Username")}
                        name="user.username"
                        value={formData.user.username}
                        color={usernameValid ? "success" : "primary"}
                        FormHelperTextProps={{
                            color: usernameValid ? "success" : "standard",
                        }}
                        InputProps={
                            usernameValid
                                ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <CheckCircleOutlineTwoTone
                                                fontSize="small"
                                                color="success"
                                            />
                                        </InputAdornment>
                                    ),
                                }
                                : {}
                        }
                        onChange={handleChange}
                        margin="normal"
                        error={errors.usernameValidationError || errors.usernameUnavailable}
                        helperText={getUsernameValidationMessage()}
                        required
                    />
                    <TextField
                        fullWidth
                        label={t("User PIN")}
                        name="operator.pin"
                        value={formData.operator.pin}
                        onChange={handleChange}
                        margin="normal"
                        error={errors.pinValidationError || errors.pinAvailableError}
                        helperText={(errors.pinValidationError || errors.pinAvailableError) && getPinValidationMessage()}
                        required
                    />
                    <TextField
                        fullWidth
                        label={t("Email Address")}
                        name="employee.email_address"
                        value={formData.employee.email_address}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label={t("Phone Number")}
                        name="employee.phone"
                        value={formData.employee.phone}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />

                    <FormGroup>
                        <FormControlLabel
                            control={<Switch />}
                            label={t("Cycle Completion Notifications")}
                            name="operator.notify_complete"
                            checked={formData.operator.notify_complete}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    operator: {
                                        ...prev.operator,
                                        notify_complete: e.target.checked,
                                    },
                                }));
                            }}
                        />
                        <FormControlLabel
                            control={<Switch />}
                            label={t("Cycle Error Notifications")}
                            name="operator.notify_error"
                            checked={formData.operator.notify_error}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    operator: {
                                        ...prev.operator,
                                        notify_error: e.target.checked,
                                    },
                                }));
                            }}
                        />
                    </FormGroup>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={
                            errors.usernameValidationError || errors.pinValidationError || errors.pinAvailableError
                        }
                        sx={{ my: 2 }}
                    >
                        {t("Submit changes")}
                    </Button>
                </>
            )}
        </Box>
    );
};

export default EditUserSettings;
